<template>
  <div
    v-if="props.downloadedStatus"
    class="flex flex-column justify-center items-center"
  >
    <h1 class="mb-10 fw-900 fs-28 lh-28 tc">Download Trip Plan</h1>
    <p class="fw-400 fs-16 lh-24 tc">
      Please enter your email below to get the trip plan.
    </p>

    <form @submit.prevent novalidate class="wp-100">
      <div class="mb-20">
        <label class="ttu fw-900 fs-11 gray-dark mb-8 dib" for="name"
          >name</label
        >
        <Input
          type="text"
          placeholder="Name"
          name="name"
          id="name"
          autocomplete="name"
          v-model="dataPost.name"
        ></Input>
        <div class="error-message" v-if="errors.name">
          {{ errors.name }}
        </div>
      </div>
      <div class="mb-20">
        <label class="ttu fw-900 fs-11 gray-dark mb-8 dib" for="email"
          >email</label
        >
        <Input
          type="text"
          placeholder="Email"
          name="email"
          id="email"
          autocomplete="email"
          v-model="dataPost.email"
        ></Input>
        <div class="error-message" v-if="errors.email">
          {{ errors.email }}
        </div>
      </div>
      <div class="mb-20">
        <label class="ttu fw-900 fs-11 gray-dark mb-8 dib" for="phone"
          >phone</label
        >
        <CountrySelect
          typeCountry="text"
          placeholderCountry="Country"
          placeholderPhone="Phone Number"
          v-model:country="dataPost.country"
          v-model:phone="dataPost.phone"
        ></CountrySelect>
        <div class="error-message" v-if="errors.phone">
          {{ errors.phone }}
        </div>
      </div>
      <div>
        <label class="ttu fw-900 fs-11 gray-dark mb-8 dib" for="email"
          >Choose pdf type</label
        >
        <div class="flex">
          <RadioBox
            label="With Images"
            value="full"
            name="option"
            :isChecked="dataPost.type === 'full'"
            class="mb-15"
            v-model:change="dataPost.type"
          />
          <RadioBox
            label="Text Only"
            value="clean"
            name="option"
            class="mb-20"
            :isChecked="dataPost.type === 'clean'"
            v-model:change="dataPost.type"
          />
        </div>
        <p class="fw-400 fs-14 lh-18 mb-30">
          By clicking download, you agree to receive news, offers and other
          promotional material via email from Designer Journeys.
        </p>
        <ButtonLoading
          v-if="loading"
          btnLabel="Get Designing"
          btnClass="button button-primary wp-100"
        />
        <button
          v-else
          type="button"
          class="button button-primary wp-100"
          @click="handleDownload"
        >
          Download PDF
        </button>
      </div>
    </form>
  </div>
  <p v-else>
    We've just sent the download link to your email. If you do not see the email
    within a few minutes, please check your Spam or Bulk E-Mail folder
  </p>
</template>

<script setup>
import { reactive, onMounted, ref } from 'vue'
import Input from '~/components/ui/input/Input.vue'
import CountrySelect from '~/components/ui/country-selectbox/CountrySelect.vue'
import RadioBox from '~/components/ui/radio-box/RadioBox.vue'
import { getCookie, setCookie } from '~/mixins/cookie'
import useFormValidation from '~/utils/validators'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { publicDomain } from '~/helper'
import tripApi from '~/composables/api/tripApi'
import countryList from '~/components/ui/country-selectbox/country.json'
import ButtonLoading from '~/components/ui/loading-button/ButtonLoading.vue'
import { debounceHeapTracking } from '~/mixins/heap'
import { debounceAmplitudeScriptTracking } from '~/mixins/amplitude'

const emit = defineEmits(['changeStatusDownload'])
const props = defineProps({
  downloadedStatus: {
    type: Boolean,
    default: false,
  },
  id: {
    type: Number,
  },
  currency: {
    type: String,
    default: 'AUD',
    required: true,
  },
  pageType: {
    type: String,
    default: 'gallery',
    validator: (value) =>
      ['gallery', 'bookable', 'preview'].includes(value.toLowerCase()),
  },
})

const dataPost = reactive({
  name: '',
  phone: '',
  email: '',
  country: '',
  type: 'full',
})

const countries = ref(countryList)
const loading = ref(false)
const {
  validateEmailField,
  validateNameField,
  errors,
  validatePhoneNumberField,
  resetErrors,
} = useFormValidation()

const route = useRoute()
const domain = publicDomain()

const handleDownload = async () => {
  try {
    loading.value = true
    validateEmailField('email', dataPost.email)
    validateNameField('name', dataPost.name)
    validatePhoneNumberField('phone', dataPost.phone, dataPost.country)

    if (!errors.name && !errors.email && !errors.phone) {
      setCookie('download_info', JSON.stringify(dataPost), 90)
      const findCountry = countries.value.find(
        (item) => item.code === dataPost.country,
      )
      const params = { ...dataPost }
      params['currency'] = props.currency
      params.ip = window.client_addr || ''
      params['version'] = props.pageType
      params['country'] = findCountry.name
      params['phone'] = `+${findCountry.dial_code}${dataPost.phone}`

      if (domain === 'agentpreview') {
        params['version'] = 'share'
        params['sharedId'] = route.params.trip
      }

      if (domain === 'agent') {
        params['version'] = 'share'
        const resp =
          route.name === 'enquiries-listing'
            ? await tripApi.getTrip(props.id)
            : await tripApi.getTrip(route.params.trip)
        // const tripData =
        //   resp && resp.data ? resp.data.data : resp.id ? resp : null
        const tripData = resp.data.value?.data
        params['sharedId'] = tripData ? tripData.shared_id : null
      }

      await tripApi.downloadPdfWithUserDetail(props.id || null, params)

      emit('changeStatusDownload')
    }
  } catch (error) {
    console.log(error)
  } finally {
    loading.value = false
    debounceHeapTracking('Download Trip Plan', {
      has_image_included: dataPost.type === 'full',
      is_sent_to_email: false,
    })
    debounceAmplitudeScriptTracking('Download Trip Plan', {
      has_image_included: dataPost.type === 'full',
      is_sent_to_email: false,
    })
  }
}

onMounted(() => {
  if (getCookie('download_info')) {
    const data = JSON.parse(getCookie('download_info'))
    const arrayKeys = Object.keys(data)
    if (arrayKeys.length) {
      arrayKeys.forEach((item) => (dataPost[item] = data[item]))
    }
  }
})

onBeforeRouteLeave(() => {
  resetErrors()
})
</script>
